import { fetchConfig, resetShimmer } from '@theme/utilities';
import { CartAddEvent } from '@theme/events';
import { morphSection } from '@theme/section-renderer';

/**
 * @returns {string[]}
 */
function getCartSectionIds() {
  /** @type {string[]} */
  const sectionIds = [];

  document.querySelectorAll('cart-items-component').forEach((component) => {
    if (component instanceof HTMLElement && component.dataset.sectionId) {
      sectionIds.push(component.dataset.sectionId);
    }
  });

  return sectionIds;
}

/**
 * @param {HTMLElement} root
 */
function bindBestiesProductRecommendation(root) {
  if (root.dataset.bestiesRecBound === 'true') return;

  root.dataset.bestiesRecBound = 'true';

  root.addEventListener('submit', async (event) => {
    const form = event.target;

    if (!(form instanceof HTMLFormElement) || !form.matches('[data-besties-rec-atc-form]')) {
      return;
    }

    event.preventDefault();

    const variantInput = form.querySelector('input[name="id"]');
    const quantityInput = form.querySelector('input[name="quantity"]');
    const submitButton = form.querySelector('button[type="submit"]');

    if (!(variantInput instanceof HTMLInputElement) || !variantInput.value) return;

    const variantId = variantInput.value;
    const quantity = quantityInput instanceof HTMLInputElement ? quantityInput.value : '1';
    const sectionIds = getCartSectionIds();
    const originalButtonText =
      submitButton instanceof HTMLButtonElement ? submitButton.textContent : '';

    if (submitButton instanceof HTMLButtonElement) {
      submitButton.disabled = true;
      submitButton.textContent = 'Adding…';
    }

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', quantity);

    // Admin-only line property (_ prefix is hidden in cart UI)
    form.querySelectorAll('input[name^="properties["]').forEach((input) => {
      if (input instanceof HTMLInputElement && input.name && input.value) {
        formData.append(input.name, input.value);
      }
    });

    if (sectionIds.length > 0) {
      formData.append('sections', sectionIds.join(','));
      formData.append('sections_url', window.location.pathname);
    }

    try {
      const response = await fetch(
        Theme.routes.cart_add_url,
        fetchConfig('javascript', { body: formData })
      );

      const data = await response.json();

      if (!response.ok || data.status || data.errors) {
        throw new Error(
          typeof data.description === 'string'
            ? data.description
            : typeof data.message === 'string'
              ? data.message
              : 'Unable to add item.'
        );
      }

      document.querySelectorAll('cart-items-component').forEach((component) => {
        if (!(component instanceof HTMLElement) || !component.dataset.sectionId) return;

        const sectionId = component.dataset.sectionId;
        const html = data.sections?.[sectionId];

        if (!html) return;

        morphSection(
          sectionId,
          html,
          component.dataset.drawer !== undefined ? 'hydration' : 'full'
        );
        resetShimmer(component);
      });

      document.dispatchEvent(
        new CartAddEvent(data, variantId, {
          source: 'besties-product-recommendation',
          itemCount: data.item_count,
          sections: data.sections,
        })
      );

      if (submitButton instanceof HTMLButtonElement) {
        submitButton.textContent = '✓ Added!';
        window.setTimeout(() => {
          submitButton.textContent = originalButtonText;
          submitButton.disabled = false;
        }, 1500);
      }
    } catch (error) {
      console.error(error);
      const message = error instanceof Error ? error.message : 'Unable to add item.';
      alert(message);

      if (submitButton instanceof HTMLButtonElement) {
        submitButton.textContent = originalButtonText;
        submitButton.disabled = false;
      }
    }
  });
}

function initAll() {
  document
    .querySelectorAll('.besties-product-recommendation-section, .besties-pairs-well-with')
    .forEach((section) => {
      if (section instanceof HTMLElement) {
        bindBestiesProductRecommendation(section);
      }
    });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAll);
} else {
  initAll();
}

document.addEventListener('shopify:section:load', (event) => {
  if (!(event.target instanceof HTMLElement)) return;

  event.target
    .querySelectorAll('.besties-product-recommendation-section, .besties-pairs-well-with')
    .forEach((section) => {
      if (section instanceof HTMLElement) {
        bindBestiesProductRecommendation(section);
      }
    });
});

document.addEventListener('shopify:block:select', (event) => {
  if (!(event.target instanceof HTMLElement)) return;

  const block = event.target.closest('.besties-pairs-well-with') || event.target.querySelector('.besties-pairs-well-with');
  if (block instanceof HTMLElement) {
    bindBestiesProductRecommendation(block);
  }
});
